import React from 'react';
import { motion } from 'framer-motion';
import { C } from '../constants';
import { useTheme } from '../contexts/ThemeContext';

// 3D Icon Base Component
const Icon3D = ({ children, color = C.secondary, size = 60 }) => {
  const { theme } = useTheme();
  
  return (
    <motion.div
      whileHover={{ 
        scale: 1.1, 
        rotateY: 15,
        rotateX: -10
      }}
      transition={{ type: 'spring', stiffness: 300 }}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        position: 'relative',
        transformStyle: 'preserve-3d',
        perspective: '1000px',
        cursor: 'pointer'
      }}
    >
      {/* Main face */}
      <div style={{
        width: '100%',
        height: '100%',
        background: `linear-gradient(135deg, ${color}40 0%, ${color}20 100%)`,
        borderRadius: '16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxShadow: `
          0 4px 20px ${color}30,
          inset 0 1px 0 rgba(255,255,255,0.2),
          inset 0 -1px 0 rgba(0,0,0,0.1)
        `,
        border: `1px solid ${color}40`,
        backdropFilter: 'blur(10px)'
      }}>
        {/* Glow effect */}
        <div style={{
          position: 'absolute',
          top: '-20%',
          left: '-20%',
          width: '140%',
          height: '140%',
          background: `radial-gradient(circle, ${color}20 0%, transparent 70%)`,
          pointerEvents: 'none',
          zIndex: 0
        }} />
        
        {/* Icon content */}
        <div style={{
          position: 'relative',
          zIndex: 1,
          fontSize: `${size * 0.5}px`,
          filter: `drop-shadow(0 2px 4px ${color}50)`
        }}>
          {children}
        </div>
      </div>
      
      {/* Bottom shadow/depth */}
      <div style={{
        position: 'absolute',
        bottom: '-4px',
        left: '10%',
        width: '80%',
        height: '10px',
        background: `${color}30`,
        borderRadius: '50%',
        filter: 'blur(8px)',
        transform: 'translateZ(-10px)'
      }} />
    </motion.div>
  );
};

// Intelligence Icon - Magnifying Glass / Brain
export const IntelligenceIcon = ({ size = 60 }) => (
  <Icon3D color="#00B4FF" size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="intel-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00B4FF" />
          <stop offset="100%" stopColor="#004AAD" />
        </linearGradient>
      </defs>
      <circle cx="11" cy="11" r="7" stroke="url(#intel-grad)" strokeWidth="2.5" fill="none" />
      <path d="M16 16L21 21" stroke="url(#intel-grad)" strokeWidth="2.5" strokeLinecap="round" />
      <circle cx="11" cy="11" r="3" fill="url(#intel-grad)" opacity="0.5" />
      <path d="M9 9L13 13M13 9L9 13" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" opacity="0.8" />
    </svg>
  </Icon3D>
);

// Consultation Icon - Expert / Target
export const ConsultationIcon = ({ size = 60 }) => (
  <Icon3D color="#0082FB" size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="consult-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0082FB" />
          <stop offset="100%" stopColor="#004AAD" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" stroke="url(#consult-grad)" strokeWidth="2" fill="none" />
      <circle cx="12" cy="12" r="6" stroke="url(#consult-grad)" strokeWidth="2" fill="none" />
      <circle cx="12" cy="12" r="2" fill="url(#consult-grad)" />
      <path d="M12 2V5M12 19V22M2 12H5M19 12H22" stroke="url(#consult-grad)" strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
    </svg>
  </Icon3D>
);

// Artefacts Icon - Lightning / Documents
export const ArtefactsIcon = ({ size = 60 }) => (
  <Icon3D color="#FFB800" size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="arte-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#FFB800" />
          <stop offset="100%" stopColor="#FF8C00" />
        </linearGradient>
      </defs>
      <path 
        d="M13 2L4 14H12L11 22L20 10H12L13 2Z" 
        fill="url(#arte-grad)" 
        stroke="url(#arte-grad)" 
        strokeWidth="1.5" 
        strokeLinejoin="round"
      />
      <path d="M13 2L4 14H12" fill="rgba(255,255,255,0.3)" />
    </svg>
  </Icon3D>
);

// Score Icon - Chart / Gauge
export const ScoreIcon = ({ size = 60 }) => (
  <Icon3D color="#00D4AA" size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="score-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00D4AA" />
          <stop offset="100%" stopColor="#00A080" />
        </linearGradient>
      </defs>
      <rect x="3" y="12" width="4" height="9" rx="1" fill="url(#score-grad)" opacity="0.6" />
      <rect x="10" y="8" width="4" height="13" rx="1" fill="url(#score-grad)" opacity="0.8" />
      <rect x="17" y="3" width="4" height="18" rx="1" fill="url(#score-grad)" />
      <path d="M5 10L12 5L19 2" stroke="url(#score-grad)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="5" cy="10" r="2" fill="#fff" />
      <circle cx="12" cy="5" r="2" fill="#fff" />
      <circle cx="19" cy="2" r="2" fill="#fff" />
    </svg>
  </Icon3D>
);

// Strategy Icon - Compass / Direction
export const StrategyIcon = ({ size = 60 }) => (
  <Icon3D color="#8B5CF6" size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="strat-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#6D28D9" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" stroke="url(#strat-grad)" strokeWidth="2" fill="none" />
      <polygon points="12,2 14,10 12,8 10,10" fill="url(#strat-grad)" />
      <polygon points="12,22 14,14 12,16 10,14" fill="url(#strat-grad)" opacity="0.5" />
      <polygon points="2,12 10,14 8,12 10,10" fill="url(#strat-grad)" opacity="0.5" />
      <polygon points="22,12 14,14 16,12 14,10" fill="url(#strat-grad)" opacity="0.5" />
      <circle cx="12" cy="12" r="2" fill="url(#strat-grad)" />
    </svg>
  </Icon3D>
);

// Platform Icon - Grid / Dashboard
export const PlatformIcon = ({ size = 60 }) => (
  <Icon3D color={C.secondary} size={size}>
    <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
      <defs>
        <linearGradient id="plat-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#0082FB" />
          <stop offset="100%" stopColor="#004AAD" />
        </linearGradient>
      </defs>
      <rect x="3" y="3" width="7" height="7" rx="2" fill="url(#plat-grad)" />
      <rect x="14" y="3" width="7" height="7" rx="2" fill="url(#plat-grad)" opacity="0.7" />
      <rect x="3" y="14" width="7" height="7" rx="2" fill="url(#plat-grad)" opacity="0.7" />
      <rect x="14" y="14" width="7" height="7" rx="2" fill="url(#plat-grad)" opacity="0.5" />
    </svg>
  </Icon3D>
);

// Export all icons
export const Icons3D = {
  Intelligence: IntelligenceIcon,
  Consultation: ConsultationIcon,
  Artefacts: ArtefactsIcon,
  Score: ScoreIcon,
  Strategy: StrategyIcon,
  Platform: PlatformIcon
};

export default Icons3D;
